// src/courses/courseProgressApi.js
// 🎓 TICKET HMXQD2 - Cliente API para sistema de progreso persistente

import apiClient from '../services/apiClient';

/**
 * Obtiene el progreso de un usuario en un curso específico
 * @param {string} courseId - ID del curso (ej: "design-thinking")
 * @returns {Promise<Object>} { courseId, completedActivities, updatedAt }
 */
export async function fetchCourseProgress(courseId) {
  try {
    console.log(`📊 [courseProgressApi] Fetching progress for course: ${courseId}`);
    
    const response = await apiClient.get(`/courses/${courseId}/progress`);
    
    if (response.data) {
      console.log(`✅ [courseProgressApi] Progress fetched:`, response.data);
      return {
        success: true,
        data: response.data
      };
    }
    
    throw new Error('Invalid response format');
  } catch (error) {
    console.error(`❌ [courseProgressApi] Error fetching progress for ${courseId}:`, error);
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Error desconocido',
      fallbackToLocalStorage: true // Señal para usar localStorage como fallback
    };
  }
}

/**
 * Marca una actividad como completada
 * @param {string} courseId - ID del curso
 * @param {string} activityId - ID de la actividad
 * @returns {Promise<Object>} { courseId, completedActivities, updatedAt }
 */
export async function completeActivity(courseId, activityId) {
  try {
    console.log(`✅ [courseProgressApi] Completing activity: ${courseId}/${activityId}`);
    
    const response = await apiClient.post(
      `/courses/${courseId}/progress/complete`,
      { activityId }
    );
    
    if (response.data) {
      console.log(`✅ [courseProgressApi] Activity completed:`, response.data);
      return {
        success: true,
        data: response.data
      };
    }
    
    throw new Error('Invalid response format');
  } catch (error) {
    console.error(`❌ [courseProgressApi] Error completing activity ${activityId}:`, error);
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Error desconocido',
      fallbackToLocalStorage: true
    };
  }
}

/**
 * Resetea el progreso de un curso (para QA/admin)
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} { courseId, completedActivities, updatedAt }
 */
export async function resetCourseProgress(courseId) {
  try {
    console.log(`🗑️ [courseProgressApi] Resetting progress for course: ${courseId}`);
    
    const response = await apiClient.post(`/courses/${courseId}/progress/reset`);
    
    if (response.data) {
      console.log(`✅ [courseProgressApi] Progress reset:`, response.data);
      return {
        success: true,
        data: response.data
      };
    }
    
    throw new Error('Invalid response format');
  } catch (error) {
    console.error(`❌ [courseProgressApi] Error resetting progress for ${courseId}:`, error);
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Error desconocido'
    };
  }
}

/**
 * Obtiene el progreso de todos los cursos del usuario
 * Útil para optimizar dashboard con una sola llamada
 * @returns {Promise<Object>} { progress: [{ courseId, completedActivities, updatedAt }] }
 */
export async function fetchAllCourseProgress() {
  try {
    console.log(`📚 [courseProgressApi] Fetching all course progress`);
    
    const response = await apiClient.get('/courses/progress/all');
    
    if (response.data && response.data.progress) {
      console.log(`✅ [courseProgressApi] All progress fetched:`, response.data.progress.length, 'courses');
      return {
        success: true,
        data: response.data.progress
      };
    }
    
    throw new Error('Invalid response format');
  } catch (error) {
    console.error(`❌ [courseProgressApi] Error fetching all progress:`, error);
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Error desconocido',
      fallbackToLocalStorage: true
    };
  }
}
